import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { SCHEMAS } from 'js/api';
import { listConnectors } from 'js/actions/connector-actions';
import {
  listTeamConnectorConfigurations,
  createTeamConnectorConfiguration,
  updateTeamConnectorConfiguration,
  deleteTeamConnectorConfiguration,
} from 'js/actions/team-connector-configuration-actions';
import { useQuery } from 'js/hooks/useQuery';
import { useNotify } from 'js/hooks/useNotify';
import EditConnectorConfiguration from '../components/EditConnectorConfiguration';

function EditConnectorConfigurationContainer(props) {
  const {
    team,
    listConnectors,
    listTeamConnectorConfigurations,
    createTeamConnectorConfiguration,
    updateTeamConnectorConfiguration,
    deleteTeamConnectorConfiguration,
  } = props;

  const { workspaceId, teamId, teamConnectorConfigurationId, connectorId } =
    useParams();
  const history = useHistory();
  const notify = useNotify();

  // Determine if this is a new configuration or editing existing
  const isNew = teamConnectorConfigurationId === 'new';
  const isNewWithConnector = isNew && connectorId;

  // Load connectors (needed to find the specific connector being configured)
  const refreshConnectorsQuery = useCallback(
    (queryId) =>
      listConnectors(workspaceId, queryId).then((x) => {
        return x;
      }),
    [workspaceId, listConnectors]
  );

  const { result: connectors, refresh: refreshConnectors } = useQuery({
    queryId: `connectors-${workspaceId}`,
    query: refreshConnectorsQuery,
    schema: SCHEMAS.CONNECTOR_ARRAY,
  });

  useEffect(() => {
    refreshConnectors();
  }, [refreshConnectors]);

  // Load team connector configurations (needed to find the specific configuration being edited)
  const refreshTeamConnectorConfigurationsQuery = useCallback(
    (queryId) => listTeamConnectorConfigurations(teamId, queryId),
    [teamId, listTeamConnectorConfigurations]
  );

  const {
    refresh: refreshTeamConnectorConfigurations,
    result: teamConnectorConfigurations,
  } = useQuery({
    queryId: `teamConnectorConfigurations-${teamId}`,
    query: refreshTeamConnectorConfigurationsQuery,
    schema: SCHEMAS.TEAM_CONNECTOR_CONFIGURATION_ARRAY,
  });

  useEffect(() => {
    refreshTeamConnectorConfigurations();
  }, [refreshTeamConnectorConfigurations]);

  // Get the current configuration being edited
  const currentConfiguration = useMemo(() => {
    return (
      (teamConnectorConfigurations ?? []).find(
        (config) => config.connector_id === teamConnectorConfigurationId
      ) ?? null
    );
  }, [teamConnectorConfigurationId, teamConnectorConfigurations]);

  // Get the connector being configured
  const currentConnector = useMemo(() => {
    if (!connectors) {
      return null;
    } else if (isNewWithConnector) {
      return connectors.find((connector) => connector.id === connectorId);
    } else if (currentConfiguration) {
      return connectors.find(
        (connector) => connector.id === currentConfiguration.connector_id
      );
    }
    return null;
  }, [isNewWithConnector, connectorId, currentConfiguration, connectors]);

  // Handle saving configuration
  const handleSaveConfiguration = useCallback(
    async (configurationData) => {
      try {
        if (currentConfiguration) {
          // Update existing configuration
          await updateTeamConnectorConfiguration(
            teamId,
            currentConfiguration.connector_id,
            { configuration: configurationData }
          );
          notify({
            type: 'success',
            message: 'Connector configuration updated successfully',
          });
        } else {
          // Create new configuration
          const targetConnectorId = currentConnector?.id || connectorId;
          if (!targetConnectorId) {
            throw new Error('No connector selected');
          }

          await createTeamConnectorConfiguration(teamId, {
            connector_id: targetConnectorId,
            configuration: configurationData,
          });
          notify({
            type: 'success',
            message: 'Connector configuration created successfully',
          });
        }

        // Refresh the team connector configurations
        await refreshTeamConnectorConfigurations();

        // Navigate back to team settings
        history.push(`/w/${workspaceId}/teams/${teamId}/settings`);
      } catch (error) {
        notify({
          type: 'danger',
          message: 'Failed to save connector configuration',
        });
        throw error;
      }
    },
    [
      currentConfiguration,
      currentConnector,
      connectorId,
      teamId,
      workspaceId,
      updateTeamConnectorConfiguration,
      createTeamConnectorConfiguration,
      refreshTeamConnectorConfigurations,
      notify,
      history,
    ]
  );

  // Handle deleting configuration
  const handleDeleteConfiguration = useCallback(async () => {
    if (!currentConfiguration) return;

    try {
      await deleteTeamConnectorConfiguration(
        teamId,
        currentConfiguration.connector_id
      );
      notify({
        type: 'success',
        message: 'Connector configuration deleted successfully',
      });

      // Refresh the team connector configurations
      await refreshTeamConnectorConfigurations();

      // Navigate back to team settings
      history.push(`/w/${workspaceId}/teams/${teamId}/settings`);
    } catch (error) {
      notify({
        type: 'danger',
        message: 'Failed to delete connector configuration',
      });
      throw error;
    }
  }, [
    currentConfiguration,
    teamId,
    workspaceId,
    deleteTeamConnectorConfiguration,
    refreshTeamConnectorConfigurations,
    notify,
    history,
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    history.push(`/w/${workspaceId}/teams/${teamId}/settings`);
  }, [history, workspaceId, teamId]);

  return (
    <EditConnectorConfiguration
      team={team}
      currentConnector={currentConnector}
      currentConfiguration={currentConfiguration}
      onSaveConfiguration={handleSaveConfiguration}
      onDeleteConfiguration={handleDeleteConfiguration}
      onCancel={handleCancel}
      isLoading={!connectors || !teamConnectorConfigurations}
    />
  );
}

export default connect(null, {
  listConnectors,
  listTeamConnectorConfigurations,
  createTeamConnectorConfiguration,
  updateTeamConnectorConfiguration,
  deleteTeamConnectorConfiguration,
})(EditConnectorConfigurationContainer);
